/**
 * Direct Delivery Orders — pure logic (PRD.265).
 *
 * Dependency-free helpers for line amounts, effective unit cost, totals,
 * validation, and reversal guards. Import-free so they can be unit-tested with
 * Node's built-in runner and reused by the direct-receipt UI + service.
 *
 * Costing rule (PRD.265 §7, §12): bonus quantity increases stock but NEVER the
 * supplier invoice amount, so `line_amount = quantity_received * unit_price`.
 * The effective unit cost spreads that amount over the total received stock.
 */

/** Line amount = quantity received × unit price. Bonus is free (not billed). */
export function computeDirectLineAmount(quantityReceived: number, unitPrice: number): number {
  const amount = (Number(quantityReceived) || 0) * (Number(unitPrice) || 0);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

/** Total stock received into inventory = quantity received + bonus. */
export function totalStockIn(quantityReceived: number, bonusQuantity: number): number {
  return (Number(quantityReceived) || 0) + (Number(bonusQuantity) || 0);
}

/**
 * Effective unit cost = line amount / (quantity received + bonus), when total
 * stock > 0 (PRD.265 §13). Returns 0 otherwise.
 */
export function computeEffectiveUnitCost(
  lineAmount: number,
  quantityReceived: number,
  bonusQuantity: number,
): number {
  const total = totalStockIn(quantityReceived, bonusQuantity);
  if (total <= 0) return 0;
  const cost = (Number(lineAmount) || 0) / total;
  return Number.isFinite(cost) && cost > 0 ? cost : 0;
}

export interface DirectReceiptLineInput {
  hasItem: boolean;
  quantityReceived: number;
  bonusQuantity: number;
  unitPrice: number;
  batchTracked: boolean;
  batchNo: string | null;
  expiryDate: string | null;
  /** Item requires an expiry date (medicine, or clinic policy). */
  requiresExpiry?: boolean;
}

/**
 * Validate a single direct-receipt line (PRD.265 §20). Returns a user-facing
 * error string or null. Free samples (qty 0, bonus > 0) are allowed.
 */
export function validateDirectReceiptLine(line: DirectReceiptLineInput): string | null {
  if (!line.hasItem) return 'Select an item for each line.';
  const qty = Number(line.quantityReceived) || 0;
  const bonus = Number(line.bonusQuantity) || 0;
  const price = Number(line.unitPrice) || 0;
  if (qty < 0) return 'Quantity Received cannot be negative.';
  if (bonus < 0) return 'Bonus Quantity cannot be negative.';
  if (price < 0) return 'Unit Price cannot be negative.';
  if (qty <= 0 && bonus <= 0) {
    return 'Enter a Quantity Received or a Bonus Quantity greater than 0.';
  }
  if (line.batchTracked && !line.batchNo?.trim()) {
    return 'Batch No. is required for batch-tracked items.';
  }
  if ((line.batchTracked || line.requiresExpiry) && !line.expiryDate) {
    return 'Expiry Date is required for this item.';
  }
  return null;
}

export interface DoTotals {
  subtotal: number;
  taxAmount: number;
  total: number;
}

/**
 * Header totals from line amounts (PRD.265 §14). Bonus never affects these.
 * `amountsAre` is 'tax_exclusive' (tax added on top) or 'tax_inclusive' (tax
 * already embedded in the line amounts).
 */
export function computeDoTotals(
  lineAmounts: readonly number[],
  taxRatePercent: number | null | undefined,
  amountsAre: string | null | undefined,
): DoTotals {
  const sum = lineAmounts.reduce((s, a) => s + (Number.isFinite(a) ? Math.max(0, a) : 0), 0);
  const rate = Number(taxRatePercent) || 0;
  if (rate <= 0) return { subtotal: sum, taxAmount: 0, total: sum };
  if (amountsAre === 'tax_inclusive') {
    const net = sum / (1 + rate / 100);
    return { subtotal: net, taxAmount: sum - net, total: sum };
  }
  const tax = sum * (rate / 100);
  return { subtotal: sum, taxAmount: tax, total: sum + tax };
}

/** A Delivery Order can be finalized only from draft with at least one line. */
export function canFinalizeDeliveryOrder(input: { status: string | null; lineCount: number }): {
  ok: boolean;
  error?: string;
} {
  if (input.status === 'received')
    return { ok: false, error: 'This delivery order is already finalized.' };
  if (input.status === 'cancelled')
    return { ok: false, error: 'This delivery order has been cancelled.' };
  if (input.lineCount <= 0) return { ok: false, error: 'Add at least one line before finalizing.' };
  return { ok: true };
}

/** A Delivery Order can be reversed only when finalized (received). */
export function canReverseDeliveryOrder(input: { status: string | null }): {
  ok: boolean;
  error?: string;
} {
  if (input.status !== 'received') {
    return { ok: false, error: 'Only a finalized delivery order can be reversed.' };
  }
  return { ok: true };
}

/**
 * Whether reversing `quantity` would drive available stock negative — used to
 * block reversal when received stock has already been consumed (PRD.265 §11).
 */
export function reversalWouldGoNegative(available: number, quantity: number): boolean {
  return (Number(available) || 0) - (Number(quantity) || 0) < 0;
}

/**
 * A Delivery Order can be deleted (soft-cancelled) from either draft or
 * received. A draft carries no stock so it just gets marked cancelled; a
 * received DO reverses stock via compensating movements (see the drawer's
 * per-line availability guard — blocked when stock was already consumed).
 * An already-cancelled DO is a no-op.
 */
export function canDeleteDeliveryOrder(input: { status: string | null }): {
  ok: boolean;
  error?: string;
} {
  if (input.status === 'cancelled') {
    return { ok: false, error: 'This delivery order is already cancelled.' };
  }
  return { ok: true };
}

/**
 * A Purchase Order carries no stock itself, but its linked Delivery Orders may.
 * Deletion (soft-cancel) is blocked while any linked DO is still active
 * (draft or received) — the user must delete/reverse those first so no stock
 * is left dangling. An already-cancelled PO is a no-op.
 */
export function canDeletePurchaseOrder(input: {
  status: string | null;
  deliveries: { status: string | null }[];
}): { ok: boolean; error?: string } {
  if (input.status === 'cancelled') {
    return { ok: false, error: 'This purchase order is already cancelled.' };
  }
  const active = input.deliveries.filter((d) => d.status !== 'cancelled');
  if (active.length > 0) {
    const received = active.filter((d) => d.status === 'received').length;
    const detail =
      received > 0
        ? `${received} received and ${active.length - received} draft`
        : `${active.length} draft`;
    return {
      ok: false,
      error: `Delete or reverse the linked delivery order(s) first (${detail}).`,
    };
  }
  return { ok: true };
}
